import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api, fmt, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, StatusBadge, EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function parseMonthDate(m) {
  if (!m) return 0;
  const p = String(m).split(' ');
  return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]);
}

// ─── Slide-out panel ──────────────────────────────────────────────────────────
function SlidePanel({ open, onClose, title, subtitle, children, width = 560 }) {
  if (!open) return null;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', justifyContent:'flex-end' }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.3)' }} onClick={onClose} />
      <div style={{ position:'relative', width, maxWidth:'90vw', height:'100%', background:'#fff', boxShadow:'-4px 0 24px rgba(0,0,0,0.12)', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'var(--navy)' }}>{title}</div>
            {subtitle && <div style={{ fontSize:12, color:'var(--text-tertiary)', marginTop:2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text-tertiary)', padding:'2px 6px' }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Main Branches Component ──────────────────────────────────────────────────
export default function Branches({ user, directorView }) {
  const [franchises, setFranchises] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [royalties, setRoyalties] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [editPanel, setEditPanel] = useState(null);
  const [showToast, Toast] = useToast();
  const [revenueYear, setRevenueYear] = useState(new Date().getFullYear());
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';

  const load = useCallback(() => {
    Promise.all([api.getFranchises(directorView), api.getDeclarations(), api.getRoyalties(), api.getUsers()])
      .then(([f, d, r, u]) => {
        setFranchises(f || []);
        setDeclarations(d || []);
        setRoyalties(r || []);
        setUsers(u || []);
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selected) {
      const updated = franchises.find(f => f.id === selected.id);
      if (updated) setSelected(updated);
    } else {
      const hash = window.location.hash.replace('#', '');
      const match = hash.match(/^branches:([a-f0-9-]{36})$/);
      if (match && franchises.length > 0) {
        const found = franchises.find(f => f.id === match[1]);
        if (found) setSelected(found);
      }
    }
  }, [franchises]);

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Branches" /><Spinner /></div>;

  if (selected) {
    return (
      <BranchDetail
        franchise={selected}
        franchises={franchises}
        declarations={declarations.filter(d => d.franchiseId === selected.id)}
        royalties={royalties.filter(r => r.franchiseId === selected.id || r.franchise_id === selected.id)}
        allDeclarations={declarations}
        users={users}
        user={user}
        isAdmin={isAdmin}
        onBack={() => { setSelected(null); window.location.hash = 'branches'; }}
        onReload={load}
        showToast={showToast}
        Toast={Toast}
      />
    );
  }

  // Search includes person names, email, location
  const filtered = franchises.filter(f => {
    const q = search.toLowerCase();
    if (!q && filter === 'all') return true;
    const matchSearch = !q || (f.name||'').toLowerCase().includes(q) || (f.location||'').toLowerCase().includes(q) ||
      (f.owners||[]).some(o => (o.name||'').toLowerCase().includes(q) || (o.email||'').toLowerCase().includes(q)) ||
      (f.investors||[]).some(i => (i.name||'').toLowerCase().includes(q) || (i.email||'').toLowerCase().includes(q));
    const matchFilter = filter === 'all' || f.status === filter;
    return matchSearch && matchFilter;
  });

  const counts = {
    all: franchises.length,
    active: franchises.filter(f => f.status === 'active').length,
    signed: franchises.filter(f => f.status === 'signed').length,
    potential: franchises.filter(f => f.status === 'potential').length,
    closed: franchises.filter(f => f.status === 'closed').length,
  };

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Branches" subtitle={`${franchises.length} total`} actions={
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:4, background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:'4px 8px' }}>
            <button onClick={()=>setRevenueYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:16, padding:'0 4px' }}>‹</button>
            <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)', minWidth:36, textAlign:'center' }}>{revenueYear}</span>
            <button onClick={()=>setRevenueYear(y=>y<new Date().getFullYear()?y+1:y)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:16, padding:'0 4px' }}>›</button>
          </div>
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setEditPanel({ mode:'new' })}>
              <i className="ti ti-plus" style={{ fontSize:14 }} /> Add Branch
            </button>
          )}
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ flex:1, maxWidth:340 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search name, location, owner, email..." />
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {[
              { key:'all', label:'All', count:counts.all },
              { key:'active', label:'Active', count:counts.active },
              { key:'signed', label:'Signed', count:counts.signed },
              { key:'potential', label:'Potential', count:counts.potential },
              { key:'closed', label:'Closed', count:counts.closed },
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} className="btn btn-sm" style={{
                background: filter === f.key ? 'var(--navy)' : '#fff',
                color: filter === f.key ? '#fff' : 'var(--text-secondary)',
                border:'1px solid var(--border)',
              }}>{f.label} <span style={{ opacity:0.6 }}>({f.count})</span></button>
            ))}
          </div>
        </div>

        <div className="card" style={{ overflow:'hidden' }}>
          {filtered.length === 0 ? <EmptyState icon="ti-building-store" message="No branches found" /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>BRANCH (LOCATION)</th><th>COMPANY</th><th>STATUS</th><th>OWNERS</th>
                  <th>LAST DECLARATION</th>
                  <th>REVENUE {revenueYear}</th>
                  <th>EXPENSES {revenueYear}</th>
                  <th>NET {revenueYear}</th>
                  <th>MARGIN</th>
                  <th>ROYALTY {revenueYear}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(f => {
                  const lastDecl = declarations.filter(d => d.franchiseId === f.id && d.status === 'approved')
                    .sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month))[0];
                  const royaltyTotal = royalties.filter(r => r.franchiseId===f.id||r.franchise_id===f.id)
                    .reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);
                  const statusMap = { active:'green', signed:'blue', potential:'amber', closed:'red' };
                  const owners = (f.owners||[]).filter(o => o.isCurrent !== false);

                  // Year figures from financials
                  const yearFinancials = (f.financials||[]).filter(m => (m.month||'').includes(String(revenueYear)));
                  const yearRevenue = yearFinancials.reduce((s,m) => s+(parseFloat(m.income)||0), 0);
                  const yearExpenses = yearFinancials.reduce((s,m) => s+(parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0), 0);
                  const yearNet = yearRevenue - yearExpenses;
                  const yearMargin = yearRevenue > 0 ? (yearNet/yearRevenue*100).toFixed(1) : null;
                  const yearRoyalty = royalties.filter(r => (r.franchiseId===f.id||r.franchise_id===f.id) && (r.month||'').includes(String(revenueYear))).reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);

                  // Branch = location, Company = name
                  const branchDisplay = f.location || f.name;
                  const companyDisplay = f.location ? f.name : null;

                  return (
                    <tr key={f.id} style={{ cursor:'pointer' }} onClick={() => { setSelected(f); window.location.hash = 'branches:' + f.id; }}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:34, height:34, borderRadius:'var(--r-md)', background:'var(--blue-bg)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'var(--navy)', flexShrink:0 }}>
                            {branchDisplay?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div style={{ fontWeight:600, fontSize:13 }}>{branchDisplay}</div>
                        </div>
                      </td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{companyDisplay||'—'}</td>
                      <td><Badge type={statusMap[f.status]||'navy'}>{f.status||'—'}</Badge></td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>
                        {owners.slice(0,2).map(o=>o.name).join(', ')||'—'}
                        {owners.length > 2 && <span style={{ color:'var(--text-tertiary)' }}> +{owners.length-2}</span>}
                      </td>
                      <td style={{ fontSize:12 }}>
                        {lastDecl ? (
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ fontWeight:500 }}>{lastDecl.month}</span>
                            <StatusBadge status={lastDecl.status} />
                          </div>
                        ) : <span style={{ color:'var(--text-tertiary)' }}>None</span>}
                      </td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color: yearRevenue > 0 ? 'var(--navy)' : 'var(--text-tertiary)' }}>
                        {yearRevenue > 0 ? fmt(yearRevenue) : '—'}
                      </td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color: yearExpenses > 0 ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>
                        {yearExpenses > 0 ? fmt(yearExpenses) : '—'}
                      </td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color: yearRevenue>0?(yearNet>=0?'var(--green)':'var(--red)'):'var(--text-tertiary)' }}>
                        {yearRevenue > 0 ? fmt(yearNet) : '—'}
                      </td>
                      <td style={{ fontSize:12, fontWeight:600, color: yearMargin!==null?(parseFloat(yearMargin)>=40?'var(--green)':parseFloat(yearMargin)>=20?'var(--amber)':'var(--red)'):'var(--text-tertiary)' }}>
                        {yearMargin !== null ? `${yearMargin}%` : '—'}
                      </td>
                      <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color: yearRoyalty > 0 ? 'var(--green)' : 'var(--text-tertiary)' }}>
                        {yearRoyalty > 0 ? fmt(yearRoyalty) : '—'}
                      </td>
                      <td><i className="ti ti-chevron-right" style={{ fontSize:16, color:'var(--text-tertiary)' }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {editPanel?.mode === 'new' && (
        <NewBranchPanel users={users} onClose={() => setEditPanel(null)} onSave={async (data) => {
          await api.createFranchise(data);
          await api.logActivity('Created branch', data.name);
          showToast('Branch created'); setEditPanel(null); load();
        }} />
      )}
    </div>
  );
}

// ─── Branch Detail ─────────────────────────────────────────────────────────────
function BranchDetail({ franchise: f, franchises, declarations, royalties, allDeclarations, users, user, isAdmin, onBack, onReload, showToast, Toast }) {
  const [panel, setPanel] = useState(null);
  const [declYear, setDeclYear] = useState(new Date().getFullYear());
  const [royaltyYear, setRoyaltyYear] = useState(new Date().getFullYear());
  const [royaltyEdit, setRoyaltyEdit] = useState(null); // { id, method, datePaid }
  const [financialsYear, setFinancialsYear] = useState(new Date().getFullYear());
  const [salaries, setSalaries] = useState([]);
  const [shareModal, setShareModal] = useState(false);
  const [shareStep, setShareStep] = useState(1);
  const [shareForm, setShareForm] = useState({ effectiveFrom:'', shares:[] });
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const [declDetailModal, setDeclDetailModal] = useState(null);

  useEffect(() => { api.getFranchiseSalaries(f.id).then(s => setSalaries(s||[])); }, [f.id]);

  const owners = (f.owners||[]).filter(o => o.isCurrent !== false);
  const allOwners = f.owners||[];
  const investors = (f.investors||[]).filter(i => i.isCurrent !== false);
  const headUser = users.find(u => u.id === f.headOfCentre);
  const statusMap = { active:'green', signed:'blue', potential:'amber', closed:'red' };
  const totalRoy = royalties.reduce((s,r) => s+(parseFloat(r.royalty)||0), 0);

  function openShareModal() {
    const shares = [
      ...owners.map(o => ({ type:'owner', name:o.name, email:o.email, phone:o.phone||'', sharePercent:String(o.sharePercent), selected:false, isNew:false })),
      ...investors.map(i => ({ type:'investor', name:i.name||i.companyName, email:i.email, phone:i.phone||'', sharePercent:String(i.sharePercent), selected:false, isNew:false })),
    ];
    setShareForm({ effectiveFrom:'', shares });
    setShareStep(1);
    setShareModal(true);
  }

  async function saveShareChange() {
    if (!shareForm.effectiveFrom) { showToast('Please set effective from month'); return; }
    const total = shareForm.shares.reduce((s,x) => s+(parseFloat(x.sharePercent)||0), 0);
    if (Math.abs(total-100) > 0.01) { showToast(`Shares must total 100%. Currently: ${total.toFixed(1)}%`); return; }

    const newOwners = shareForm.shares.filter(s => s.type==='owner' && s.isNew);
    const existingOwners = shareForm.shares.filter(s => s.type==='owner' && !s.isNew);
    const invChanges = shareForm.shares.filter(s => s.type==='investor' && s.selected);

    const promises = [];
    if (newOwners.length) {
      newOwners.forEach(o => promises.push(api.addFranchiseOwner(f.id, { name:o.name, email:o.email, phone:o.phone, sharePercent:o.sharePercent, effectiveFrom:shareForm.effectiveFrom })));
    }
    if (existingOwners.length) {
      promises.push(api.changeShares(f.id, { effectiveFrom:shareForm.effectiveFrom, changes:existingOwners.map(s=>({ name:s.name, email:s.email, phone:s.phone||'', sharePercent:s.sharePercent })) }));
    }
    if (invChanges.length) {
      promises.push(api.changeInvestorShares(f.id, { effectiveFrom:shareForm.effectiveFrom, changes:invChanges.map(s=>({ name:s.name, email:s.email, sharePercent:s.sharePercent })) }));
    }
    await Promise.all(promises);
    await api.logActivity('Changed shares', `${f.name} — effective ${shareForm.effectiveFrom}`);
    showToast('Shares updated'); setShareModal(false); onReload();
  }

  // Salary helpers
  function getSalary(ownerId, month) {
    const ownerSals = salaries.filter(s => s.userId === ownerId);
    if (!ownerSals.length) return 0;
    const applicable = ownerSals.filter(s => parseMonthDate(s.effectiveFrom) <= parseMonthDate(month)).sort((a,b) => parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom));
    return applicable.length ? parseFloat(applicable[0].amount)||0 : 0;
  }
  function getOwnerUserId(owner) {
    return users.find(u => (u.email||'').toLowerCase()===(owner.email||'').toLowerCase())?.id || null;
  }

  // P&L data — use declaration figures when available (approved declarations have up-to-date
  // figures including any approved amendments; financials may lag if backend wasn't redeployed)
  const plFinancials = (f.financials||[]).filter(m => (m.month||'').includes(String(plYear))).sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month)).map(m => {
    const decl = declarations.find(d => d.month === m.month && d.status === 'approved');
    if (!decl) return m;
    // Amended values take priority; fall back to declaration's own figures (which edit-request-approve
    // overwrites with the amended values), then financials as last resort
    return {
      ...m,
      income:        decl.amendedIncome        != null ? decl.amendedIncome        : (decl.income        != null ? decl.income        : m.income),
      salaries:      decl.amendedSalaries      != null ? decl.amendedSalaries      : (decl.salaries      != null ? decl.salaries      : m.salaries),
      rent:          decl.amendedRent          != null ? decl.amendedRent          : (decl.rent          != null ? decl.rent          : m.rent),
      royalty:       decl.amendedRoyalty       != null ? decl.amendedRoyalty       : (decl.royalty       != null ? decl.royalty       : m.royalty),
      otherExpenses: decl.amendedOtherExpenses != null ? decl.amendedOtherExpenses : (decl.otherExpenses != null ? decl.otherExpenses : m.otherExpenses),
    };
  });
  const yearDecls = declarations.filter(d => (d.month||'').includes(String(declYear)));

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title={f.location || f.name} subtitle={f.location ? f.name : null} actions={
        <div style={{ display:'flex', gap:8 }}>
          {isAdmin && <button className="btn btn-secondary btn-sm" onClick={openShareModal}>Change Shares</button>}
          {isAdmin && <button className="btn btn-secondary btn-sm" onClick={() => setPanel('info')}><i className="ti ti-edit" style={{ fontSize:13 }} /> Edit</button>}
          <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        </div>
      } />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:20 }}>
          {[
            { label:'Status', value:<Badge type={statusMap[f.status]||'navy'}>{f.status||'—'}</Badge> },
            { label:'Total Royalty', value:<span style={{ color:'var(--green)', fontWeight:600, fontFamily:'DM Mono,monospace' }}>{fmt(totalRoy)}</span> },
            { label:'Head of Centre', value:headUser?.name||'—' },
            { label:'Royalty Starts', value:f.royaltyStartMonth||'—' },
            { label:'Franchise #', value:f.location||'—' },
          ].map(item => (
            <div key={item.label} className="card" style={{ padding:'12px 16px' }}>
              <div style={{ fontSize:10, color:'var(--text-tertiary)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.06em' }}>{item.label}</div>
              <div style={{ fontSize:13, fontWeight:500 }}>{item.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          {/* Owners */}
          <div className="card">
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Owners</span>
              {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => setPanel('owners')}>Edit</button>}
            </div>
            {owners.length === 0 ? <EmptyState icon="ti-users" message="No owners" /> :
              owners.map((o,i) => {
                const uid = getOwnerUserId(o);
                const latestSal = salaries.filter(s=>s.userId===uid).sort((a,b)=>parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom))[0];
                return (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, display:'flex', alignItems:'center', gap:6 }}>
                        {o.name}
                        {f.headOfCentre === uid && <Badge type="blue">HoC</Badge>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{o.email}</div>
                      {latestSal && <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>Salary: {fmt(latestSal.amount)}/mo</div>}
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)' }}>{o.sharePercent}%</span>
                  </div>
                );
              })
            }
            {/* Historical owners */}
            {allOwners.filter(o=>o.isCurrent===false).length > 0 && (
              <ExpandableSection title="Share History">
                {allOwners.filter(o=>o.isCurrent===false).map((o,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'6px 16px', opacity:0.6 }}>
                    <span>{o.name} {o.effectiveFrom && <span style={{ color:'var(--text-tertiary)' }}>({o.effectiveFrom}{o.effectiveTo?` → ${o.effectiveTo}`:''})</span>}</span>
                    <span>{o.sharePercent}%</span>
                  </div>
                ))}
              </ExpandableSection>
            )}
          </div>

          {/* Investors */}
          <div className="card">
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Investors</span>
              {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => setPanel('investors')}>Edit</button>}
            </div>
            {investors.length === 0 ? <EmptyState icon="ti-briefcase" message="No investors" /> :
              investors.map((inv,i) => (
                <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500 }}>{inv.companyName||inv.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{inv.email}</div>
                  </div>
                  <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)' }}>{inv.sharePercent}%</span>
                </div>
              ))
            }
            {/* Historical investors */}
            {(f.investors||[]).filter(i=>i.isCurrent===false).length > 0 && (
              <ExpandableSection title="Share History">
                {(f.investors||[]).filter(i=>i.isCurrent===false).map((inv,i) => (
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'6px 16px', opacity:0.6 }}>
                    <span>{inv.name} {inv.effectiveFrom && <span style={{ color:'var(--text-tertiary)' }}>({inv.effectiveFrom}{inv.effectiveTo?` → ${inv.effectiveTo}`:''})</span>}</span>
                    <span>{inv.sharePercent}%</span>
                  </div>
                ))}
              </ExpandableSection>
            )}
          </div>

          {/* Royalties */}
          <div className="card">
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>
                Royalty Payments ({royalties.filter(r=>(r.month||'').includes(String(royaltyYear))).length})
              </span>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setRoyaltyYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>&#8249;</button>
                <span style={{ fontSize:12, fontWeight:500, minWidth:36, textAlign:'center' }}>{royaltyYear}</span>
                <button onClick={() => setRoyaltyYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>&#8250;</button>
              </div>
            </div>
            {royalties.filter(r=>(r.month||'').includes(String(royaltyYear))).length === 0
              ? <EmptyState icon="ti-coin" message={`No royalty payments for ${royaltyYear}`} />
              : [...royalties].filter(r=>(r.month||'').includes(String(royaltyYear))).sort((a,b)=>parseMonthDate(b.month)-parseMonthDate(a.month)).map(r => (
                <div key={r.id} style={{ borderBottom:'1px solid var(--border)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', cursor:'pointer' }}
                    onClick={() => setRoyaltyEdit(re => re?.id===r.id ? null : { id:r.id, method:r.paymentMethod||r.source||'', datePaid:r.paymentDate ? r.paymentDate.split('T')[0] : '' })}>
                    <div>
                      <div style={{ fontSize:12, fontWeight:500 }}>{r.month}</div>
                      <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{r.paymentMethod||r.source||'—'}{r.paymentDate ? ' · ' + new Date(r.paymentDate).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : ''}</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontSize:13, fontWeight:600, color:'var(--green)', fontFamily:'DM Mono,monospace' }}>{fmt(r.royalty)}</span>
                      <i className={'ti ti-chevron-' + (royaltyEdit?.id===r.id ? 'up' : 'down')} style={{ fontSize:12, color:'var(--text-tertiary)' }} />
                    </div>
                  </div>
                  {royaltyEdit?.id===r.id && (
                    <div style={{ padding:'10px 16px 14px', background:'var(--surface-1)', borderTop:'1px solid var(--border)' }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                        <div>
                          <label>PAYMENT METHOD</label>
                          <select value={royaltyEdit.method} onChange={e=>setRoyaltyEdit(re=>({...re,method:e.target.value}))}>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cash">Cash</option>
                            <option value="cheque">Cheque</option>
                            <option value="card">Card</option>
                            <option value="jotform">JotForm</option>
                            <option value="square">Square</option>
                            <option value="square_terminal">Square Terminal</option>
                            <option value="manual">Manual</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label>DATE PAID</label>
                          <input type="date" value={royaltyEdit.datePaid} onChange={e=>setRoyaltyEdit(re=>({...re,datePaid:e.target.value}))} />
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button className="btn btn-sm btn-primary" onClick={async()=>{
                          await api.updateRoyalty(r.id, { franchiseId: r.franchiseId||r.franchise_id, franchiseName: r.franchiseName||r.franchise_name||'', month: r.month, revenue: r.revenue||r.income||0, royalty: r.royalty, paymentDate: royaltyEdit.datePaid, paymentMethod: royaltyEdit.method, source: r.source||royaltyEdit.method });
                          setRoyaltyEdit(null); onReload();
                        }}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={()=>setRoyaltyEdit(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            }
          </div>

          {/* Recent declarations */}
          <div className="card">
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Declarations</span>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setDeclYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
                <span style={{ fontSize:12, fontWeight:500, minWidth:36, textAlign:'center' }}>{declYear}</span>
                <button onClick={() => setDeclYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
              </div>
            </div>
            {yearDecls.length === 0 ? <EmptyState icon="ti-file-text" message={`No declarations for ${declYear}`} /> : (
              <table className="table">
                <thead><tr><th>MONTH</th><th>INCOME</th><th>ROYALTY</th><th>NET</th><th>MARGIN</th><th>STATUS</th><th></th></tr></thead>
                <tbody>
                  {yearDecls.sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month)).map(d => {
                    const income = d.amendedIncome!=null ? parseFloat(d.amendedIncome) : parseFloat(d.income)||0;
                    const royalty = d.amendedRoyalty!=null ? parseFloat(d.amendedRoyalty) : parseFloat(d.royalty)||0;
                    const rent = d.amendedRent!=null ? parseFloat(d.amendedRent) : parseFloat(d.rent)||0;
                    const salaries = d.amendedSalaries!=null ? parseFloat(d.amendedSalaries) : parseFloat(d.salaries)||0;
                    const other = d.amendedOtherExpenses!=null ? parseFloat(d.amendedOtherExpenses) : parseFloat(d.otherExpenses)||0;
                    const exp = rent+salaries+royalty+other;
                    const net = income - exp;
                    const margin = income > 0 ? (net/income*100).toFixed(1) : 0;
                    const hasAmend = d.amendedIncome !== null && d.amendedIncome !== undefined;
                    const hasEditApproved = d.editRequestApproved && !d.editRequested;
                    return (
                      <tr key={d.id} style={{ cursor: hasAmend||hasEditApproved ? 'pointer' : 'default' }}
                        onClick={() => (hasAmend||hasEditApproved) && setDeclDetailModal(d)}
                        onMouseEnter={e => (hasAmend||hasEditApproved) && (e.currentTarget.style.background='var(--bg)')}
                        onMouseLeave={e => (e.currentTarget.style.background='')}>
                        <td style={{ fontWeight:500 }}>{d.month}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>
                          {hasAmend ? (
                            <div>
                              <div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(d.income)}</div>
                              <div style={{ fontWeight:600 }}>{fmt(d.amendedIncome)}</div>
                            </div>
                          ) : fmt(d.income)}
                        </td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(royalty)}</td>
                        <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color: net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</td>
                        <td style={{ fontSize:12, color: parseFloat(margin)>=40?'var(--green)':parseFloat(margin)>=20?'var(--amber)':'var(--red)' }}>{margin}%</td>
                        <td>
                          {hasAmend && !d.amendmentSettled ? <Badge type="amber">⚠ Amendment</Badge> :
                           hasAmend && d.amendmentSettled ? <Badge type="green">✓ Settled</Badge> :
                           hasEditApproved ? <Badge type="blue">✎ Edited</Badge> :
                           <StatusBadge status={d.status} />}
                        </td>
                        <td style={{ fontSize:11, color:'var(--text-tertiary)' }}>
                          {(hasAmend||hasEditApproved) && <i className="ti ti-chevron-right" style={{ fontSize:13 }} />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Franchisee P&L Table */}
        {owners.length > 0 && (f.financials||[]).length > 0 && (
          <div className="card" style={{ marginTop:16 }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Franchisee Profit & Loss</span>
              <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                <button onClick={()=>setPlYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
                <span style={{ fontSize:12, fontWeight:600, minWidth:36, textAlign:'center' }}>{plYear}</span>
                <button onClick={()=>setPlYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
              </div>
            </div>
            {plFinancials.length === 0 ? (
              <div style={{ padding:24, textAlign:'center', color:'var(--text-tertiary)', fontSize:13 }}>No data for {plYear}</div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>MONTH</th>
                      <th style={{ textAlign:'right' }}>NET PROFIT</th>
                      {owners.map((o,i) => (
                        <th key={i} style={{ textAlign:'right', color:'var(--green)', minWidth:120 }}>
                          {o.name.split(' ')[0]}
                          <div style={{ fontSize:9, color:'var(--text-tertiary)', fontWeight:400 }}>Salary (S) + Share</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {plFinancials.map(m => {
                      const net = (parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                      const allOwnerSals = owners.reduce((s,o) => s + getSalary(getOwnerUserId(o), m.month), 0);
                      const afterSals = net - allOwnerSals;
                      return (
                        <tr key={m.month}>
                          <td style={{ fontSize:12 }}>{m.month}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                          {owners.map((o,oi) => {
                            const uid = getOwnerUserId(o);
                            const oSal = getSalary(uid, m.month);
                            const share = afterSals * parseFloat(o.sharePercent||0)/100;
                            const th = oSal + share;
                            return (
                              <td key={oi} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11 }}>
                                <div style={{ fontWeight:700, fontSize:12, color:th>=0?'var(--green)':'var(--red)' }}>{fmt(th)}</div>
                                <div style={{ fontSize:10, color:'var(--text-tertiary)', marginTop:1 }}>
                                  {oSal > 0 && <span style={{ color:'var(--amber)' }}>(S){fmt(oSal)}</span>}
                                  {oSal > 0 && <span> + </span>}
                                  <span>{fmt(share)}</span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    {/* Year totals */}
                    <tr className="total-row" style={{ background:'var(--blue-bg)', fontWeight:700 }}>
                      <td style={{ fontSize:12, fontWeight:700, color:'var(--navy)' }}>YEARLY TOTAL {plYear}</td>
                      <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                        {fmt(plFinancials.reduce((s,m) => s+(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0)), 0))}
                      </td>
                      {owners.map((o,oi) => {
                        const uid = getOwnerUserId(o);
                        const totalTH = plFinancials.reduce((s,m) => {
                          const net = (parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                          const allSals = owners.reduce((ss,oo)=>ss+getSalary(getOwnerUserId(oo),m.month),0);
                          const oSal = getSalary(uid, m.month);
                          return s+((net-allSals)*parseFloat(o.sharePercent||0)/100+oSal);
                        },0);
                        const totalSal = plFinancials.reduce((s,m)=>s+getSalary(uid,m.month),0);
                        const totalShare = totalTH - totalSal;
                        return (
                          <td key={oi} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:11 }}>
                            <div style={{ fontWeight:700, fontSize:13, color:totalTH>=0?'var(--green)':'var(--red)' }}>{fmt(totalTH)}</div>
                            <div style={{ fontSize:10, color:'var(--text-tertiary)', marginTop:1 }}>
                              {totalSal > 0 && <span style={{ color:'var(--amber)' }}>(S){fmt(totalSal)}</span>}
                              {totalSal > 0 && <span> + </span>}
                              <span>{fmt(totalShare)}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Investor Profit & Loss Table */}
        {investors.length > 0 && plFinancials.length > 0 && (
          <InvestorPLTable
            franchise={f}
            investors={investors}
            owners={owners}
            plFinancials={plFinancials}
            plYear={plYear}
            setPlYear={setPlYear}
            getSalary={getSalary}
            getOwnerUserId={getOwnerUserId}
            isAdmin={isAdmin}
            showToast={showToast}
            onReload={onReload}
          />
        )}

        {/* Monthly Financials */}
        <div className="card" style={{ marginTop:16 }}>
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
            <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>Monthly Financials</span>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => setPanel('financials')}>+ Add Month</button>}
              {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => setPanel('salaries')}>Salaries</button>}
              {isAdmin && <button className="btn btn-sm btn-secondary" onClick={() => setPanel('bank')}>Bank Details</button>}
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setFinancialsYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
                <span style={{ fontSize:12, fontWeight:500, minWidth:36, textAlign:'center' }}>{financialsYear}</span>
                <button onClick={() => setFinancialsYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
              </div>
            </div>
          </div>
          <FinancialsTable franchise={f} year={financialsYear} isAdmin={isAdmin} onReload={onReload} showToast={showToast} declarations={declarations} onDeclClick={setDeclDetailModal} />
        </div>
      </div>

      {/* Declaration Detail Modal (amendments + edit history) */}
      {declDetailModal && (
        <Modal title={`${declDetailModal.month} — Declaration Detail`} onClose={() => setDeclDetailModal(null)} width={540}
          footer={
            <div style={{ display:'flex', gap:8, justifyContent:'space-between', width:'100%' }}>
              {declDetailModal.amendedIncome !== null && declDetailModal.amendedIncome !== undefined && !declDetailModal.amendmentSettled && isAdmin && (
                <button className="btn btn-primary" onClick={async () => {
                  await api.markAmendmentSettled(declDetailModal.id);
                  await api.logActivity('Amendment settled', `${f.location||f.name} — ${declDetailModal.month}`);
                  showToast('Amendment marked as settled');
                  setDeclDetailModal(null);
                  onReload();
                }}>✓ Mark as Settled</button>
              )}
              <button className="btn btn-secondary" onClick={() => setDeclDetailModal(null)}>Close</button>
            </div>
          }>
          <DeclDetailView d={declDetailModal} fmt={fmt} />
        </Modal>
      )}

      {/* Edit Panels */}
      <SlidePanel open={panel === 'info'} onClose={() => setPanel(null)} title="Edit Branch Info" subtitle={f.location||f.name}>
        <EditBranchInfo key={f.royaltyStartMonth+f.headOfCentre+f.status} franchise={f} users={users} owners={owners} onSave={async (data) => {
          await api.updateFranchise(f.id, data);
          await api.logActivity('Updated branch', f.name);
          showToast('Branch updated'); setPanel(null); onReload();
        }} onClose={() => setPanel(null)} />
      </SlidePanel>
      <SlidePanel open={panel === 'owners'} onClose={() => setPanel(null)} title="Edit Owners" subtitle={f.location||f.name} width={620}>
        <EditOwners franchise={f} users={users} onSave={async (data) => {
          await api.updateFranchise(f.id, data);
          await api.logActivity('Updated owners', f.name);
          showToast('Owners updated'); setPanel(null); onReload();
        }} onClose={() => setPanel(null)} />
      </SlidePanel>
      <SlidePanel open={panel === 'investors'} onClose={() => setPanel(null)} title="Edit Investors" subtitle={f.location||f.name} width={620}>
        <EditInvestors franchise={f} users={users} onSave={async (data) => {
          await api.updateFranchise(f.id, data);
          await api.logActivity('Updated investors', f.name);
          showToast('Investors updated'); setPanel(null); onReload();
        }} onClose={() => setPanel(null)} />
      </SlidePanel>
      <SlidePanel open={panel === 'financials'} onClose={() => setPanel(null)} title="Add Monthly Figures" subtitle={f.location||f.name}>
        <AddFinancials franchise={f} onSave={async (data) => {
          await api.addFinancials(f.id, data);
          await api.logActivity('Added financials', `${f.name} — ${data.month}`);
          showToast('Financials added'); setPanel(null); onReload();
        }} onClose={() => setPanel(null)} />
      </SlidePanel>
      <SlidePanel open={panel === 'salaries'} onClose={() => setPanel(null)} title="Manage Salaries" subtitle={f.location||f.name} width={640}>
        <ManageSalaries franchise={f} owners={owners} salaries={salaries} users={users} onSave={async (data) => {
          await api.updateFranchiseSalaries(f.id, data);
          await api.logActivity('Updated salaries', f.name);
          showToast('Salaries updated'); setPanel(null);
          api.getFranchiseSalaries(f.id).then(s => setSalaries(s||[]));
        }} onClose={() => setPanel(null)} />
      </SlidePanel>
      <SlidePanel open={panel === 'bank'} onClose={() => setPanel(null)} title="Bank Details" subtitle={f.location||f.name}>
        <BankDetailsPanel franchise={f} onClose={() => setPanel(null)} onReload={onReload} showToast={showToast} />
      </SlidePanel>

      {/* Change Shares Modal */}
      {shareModal && (
        <Modal title="Change Ownership Shares" onClose={() => setShareModal(false)} width={500}
          footer={
            shareStep === 1 ? (
              <>
                <button className="btn btn-secondary" onClick={() => setShareModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => {
                  if (!shareForm.shares.some(s=>s.selected)) { showToast('Select at least one person'); return; }
                  setShareStep(2);
                }}>Next →</button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => setShareStep(1)}>← Back</button>
                <button className="btn btn-primary" onClick={saveShareChange}>Save Changes</button>
              </>
            )
          }>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:16 }}>{f.name}</p>

          {shareStep === 1 && (
            <div>
              <p style={{ fontSize:13, marginBottom:14, color:'var(--navy)' }}>Who are you changing shares for?</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                {shareForm.shares.map((s,i) => (
                  <div key={i} onClick={() => setShareForm(f => { const ns=[...f.shares]; ns[i]={...ns[i],selected:!ns[i].selected}; return {...f,shares:ns}; })}
                    style={{ padding:'12px 14px', borderRadius:'var(--r-md)', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center',
                      background:s.selected?'var(--blue-bg)':'#f9fafb', border:`1px solid ${s.selected?'var(--navy)':'var(--border)'}`, borderLeft:`3px solid ${s.selected?'var(--navy)':'var(--border)'}` }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:20, height:20, borderRadius:4, border:`2px solid ${s.selected?'var(--navy)':'var(--border)'}`, background:s.selected?'var(--navy)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        {s.selected && <span style={{ color:'#fff', fontSize:12 }}>✓</span>}
                      </div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{s.name}</div>
                        <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{s.type==='owner'?'Franchisee':'Investor'} · Current: {s.sharePercent}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Add new franchisee */}
              <div style={{ borderTop:'1px solid var(--border)', paddingTop:12 }}>
                <p style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:8 }}>Adding a new franchisee to this branch?</p>
                <AddNewPersonRow onAdd={person => setShareForm(f=>({...f,shares:[...f.shares,{...person,selected:true,isNew:true}]}))} showToast={showToast} />
              </div>
            </div>
          )}

          {shareStep === 2 && (
            <div>
              <div style={{ marginBottom:14 }}>
                <label>EFFECTIVE FROM (MONTH)</label>
                <input value={shareForm.effectiveFrom} placeholder="e.g. Jun 2026" onChange={e=>setShareForm(f=>({...f,effectiveFrom:e.target.value}))} />
              </div>
              <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:12 }}>Edit new share % for selected people.</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                {shareForm.shares.map((s,i) => (
                  <div key={i} style={{ padding:'12px 14px', borderRadius:'var(--r-md)', background:'#f9fafb', borderLeft:`3px solid ${s.selected?'var(--navy)':'var(--border)'}`, opacity:s.selected?1:0.5 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{s.name}</div>
                        <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{s.type==='owner'?'Franchisee':'Investor'}</div>
                      </div>
                      {s.selected ? (
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <input type="number" min="0" max="100" step="0.5" placeholder="%" value={s.sharePercent}
                            onChange={e=>setShareForm(f=>{const ns=[...f.shares];ns[i]={...ns[i],sharePercent:e.target.value};return {...f,shares:ns};})}
                            style={{ width:70, padding:'6px 8px', fontSize:13, textAlign:'center', marginBottom:0 }} />
                          <span style={{ fontSize:13, color:'var(--text-tertiary)' }}>%</span>
                        </div>
                      ) : (
                        <span style={{ fontSize:13, color:'var(--text-tertiary)', fontWeight:600 }}>{s.sharePercent}% (unchanged)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {(() => {
                const total = shareForm.shares.reduce((s,x)=>s+(parseFloat(x.sharePercent)||0),0);
                const ok = Math.abs(total-100)<0.01;
                return (
                  <div style={{ padding:'12px 14px', borderRadius:'var(--r-md)', background:ok?'var(--green-bg)':'var(--amber-bg)', border:`1px solid ${ok?'rgba(15,110,86,0.2)':'rgba(217,119,6,0.2)'}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)' }}>TOTAL</span>
                    <span style={{ fontSize:16, fontWeight:800, color:ok?'var(--green)':'var(--amber)' }}>{total.toFixed(1)}% {ok?'✓':'≠ 100%'}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function AddNewPersonRow({ onAdd, showToast }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [share, setShare] = useState('');
  return (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      <input placeholder="Full name" value={name} onChange={e=>setName(e.target.value)} style={{ flex:1, minWidth:120, marginBottom:0 }} />
      <input placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} style={{ flex:1, minWidth:140, marginBottom:0 }} />
      <input type="number" placeholder="Share %" value={share} onChange={e=>setShare(e.target.value)} style={{ width:70, marginBottom:0 }} />
      <button className="btn btn-sm btn-primary" onClick={() => {
        if (!name.trim()) { showToast('Please enter a name'); return; }
        onAdd({ type:'owner', name:name.trim(), email:email.trim(), phone:'', sharePercent:share });
        setName(''); setEmail(''); setShare('');
        showToast(`${name} added — ticked and ready for step 2`);
      }}>+ Add</button>
    </div>
  );
}

function ExpandableSection({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={()=>setOpen(o=>!o)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'var(--text-tertiary)', display:'flex', alignItems:'center', gap:4, padding:'8px 16px', width:'100%', textAlign:'left' }}>
        {title} {open?'▲':'▼'}
      </button>
      {open && <div style={{ borderTop:'1px solid var(--border)' }}>{children}</div>}
    </div>
  );
}

// ─── Financials Table ──────────────────────────────────────────────────────────
function FinancialsTable({ franchise, year, isAdmin, onReload, showToast, declarations, onDeclClick }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  // Find declarations with amendments for this franchise
  const amendedDecls = (declarations||[]).filter(d =>
    d.amendedIncome !== null && d.amendedIncome !== undefined
  );

  const yearFinancials = (franchise.financials||[])
    .filter(m => (m.month||'').includes(String(year)))
    .sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month));

  if (yearFinancials.length === 0) return (
    <div style={{ padding:32, textAlign:'center', color:'var(--text-tertiary)', fontSize:13 }}>
      No financial data for {year}. {isAdmin && 'Click "Add Month" to add figures.'}
    </div>
  );

  const totals = yearFinancials.reduce((s,m) => ({
    income: s.income+(parseFloat(m.income)||0),
    rent: s.rent+(parseFloat(m.rent)||0),
    salaries: s.salaries+(parseFloat(m.salaries)||0),
    royalty: s.royalty+(parseFloat(m.royalty)||0),
    other: s.other+(parseFloat(m.otherExpenses)||0),
    net: s.net+((parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0))),
  }), { income:0, rent:0, salaries:0, royalty:0, other:0, net:0 });

  return (
    <table className="table">
      <thead><tr>
        <th>MONTH</th><th>INCOME</th><th>RENT</th><th>SALARIES</th><th>ROYALTY</th><th>OTHER</th><th>NET PROFIT</th><th>MARGIN</th>
        {isAdmin && <th></th>}
      </tr></thead>
      <tbody>
        {yearFinancials.map(m => {
          const exp = (parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0);
          const net = (parseFloat(m.income)||0)-exp;
          const margin = m.income > 0 ? (net/parseFloat(m.income)*100).toFixed(1) : 0;
          const isEdit = editing === m.month;
          if (isEdit) return (
            <tr key={m.month} style={{ background:'var(--blue-bg)' }}>
              <td style={{ fontWeight:500 }}>{m.month}</td>
              {['income','rent','salaries','royalty','otherExpenses'].map(k => (
                <td key={k}><input type="number" value={form[k]||''} onChange={e=>setForm(f=>({...f,[k]:e.target.value}))} style={{ width:80, padding:'4px 8px', fontSize:12, marginBottom:0 }} /></td>
              ))}
              <td colSpan={2}>
                <div style={{ display:'flex', gap:6 }}>
                  <button className="btn btn-sm btn-primary" onClick={async () => {
                    await api.updateFinancials(franchise.id, m.month, form);
                    await api.logActivity('Updated financials', franchise.name+' — '+m.month);
                    showToast('Updated'); setEditing(null); onReload();
                  }}>Save</button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </td>
            </tr>
          );
          return (
            <tr key={m.month} style={{ cursor: amendedDecls.find(d=>d.month===m.month) ? 'pointer' : 'default' }}
              onClick={() => {
                const d = amendedDecls.find(dd=>dd.month===m.month);
                if (d && onDeclClick) onDeclClick(d);
              }}>
              <td style={{ fontWeight:500 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  {m.month}
                  {(() => {
                    const d = amendedDecls.find(dd=>dd.month===m.month);
                    if (!d) return null;
                    return d.amendmentSettled
                      ? <Badge type="green">✓ Settled</Badge>
                      : <Badge type="amber">⚠ Amendment</Badge>;
                  })()}
                </div>
              </td>
              {['income','rent','salaries','royalty','otherExpenses'].map(field => {
                const amendKey = 'amended' + field.charAt(0).toUpperCase() + field.slice(1);
                const amended = m[amendKey] !== null && m[amendKey] !== undefined;
                return (
                  <td key={field} style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>
                    {amended ? (
                      <div>
                        <div style={{ textDecoration:'line-through', color:'var(--text-tertiary)', fontSize:11 }}>{fmt(m[field])}</div>
                        <div style={{ fontWeight:600, color:'var(--blue)' }}>{fmt(m[amendKey])}</div>
                      </div>
                    ) : fmt(m[field])}
                  </td>
                );
              })}
              <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</td>
              <td style={{ fontSize:12, color:parseFloat(margin)>=40?'var(--green)':parseFloat(margin)>=20?'var(--amber)':'var(--red)' }}>{margin}%</td>
              {isAdmin && (
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditing(m.month); setForm({income:m.income,rent:m.rent,salaries:m.salaries,royalty:m.royalty,otherExpenses:m.otherExpenses}); }}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={async () => {
                      if (!window.confirm(`Delete ${m.month}?`)) return;
                      await api.deleteFinancials(franchise.id, m.month); showToast('Deleted'); onReload();
                    }}>Del</button>
                  </div>
                </td>
              )}
            </tr>
          );
        })}
        <tr className="total-row" style={{ background:'var(--blue-bg)', fontWeight:600 }}>
          <td>TOTAL {year}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--navy)' }}>{fmt(totals.income)}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(totals.rent)}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(totals.salaries)}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(totals.royalty)}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(totals.other)}</td>
          <td style={{ fontFamily:'DM Mono,monospace', fontSize:12, color:totals.net>=0?'var(--green)':'var(--red)' }}>{fmt(totals.net)}</td>
          <td style={{ fontSize:12 }}>{totals.income>0?(totals.net/totals.income*100).toFixed(1):0}%</td>
          {isAdmin && <td></td>}
        </tr>
      </tbody>
    </table>
  );
}

// ─── Edit Branch Info ──────────────────────────────────────────────────────────
function EditBranchInfo({ franchise, users, owners, onSave, onClose }) {
  const [form, setForm] = useState({
    name:franchise.name||'', location:franchise.location||'', phone:franchise.phone||'', email:franchise.email||'',
    status:franchise.status||'potential', headOfCentre:franchise.headOfCentre||'',
    royaltyStartMonth:franchise.royaltyStartMonth||'', closedDate:franchise.closedDate||'',
  });
  const u = e => setForm(p=>({...p,...e}));

  // Royalty months
  const MTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const now2 = new Date();
  const allMonths = [];
  const futureDate = new Date(now2.getFullYear(), now2.getMonth() + 6);
  for (let y = 2024; y <= futureDate.getFullYear(); y++) {
    const endM = y === futureDate.getFullYear() ? futureDate.getMonth() : 11;
    for (let m = 0; m <= endM; m++) allMonths.push(`${MTHS[m]} ${y}`);
  }

  // All users for HOC dropdown
  const allFranchiseeUsers = users.filter(u => u.role === 'franchisee' || u.role === 'director');

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div><label>COMPANY NAME</label><input value={form.name} onChange={e=>u({name:e.target.value})} /></div>
      <div><label>LOCATION</label><input value={form.location} onChange={e=>u({location:e.target.value})} /></div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div><label>PHONE</label><input value={form.phone} onChange={e=>u({phone:e.target.value})} /></div>
        <div><label>EMAIL</label><input value={form.email} onChange={e=>u({email:e.target.value})} /></div>
      </div>

      <div><label>ROYALTY START MONTH</label>
        <select value={form.royaltyStartMonth} onChange={e=>u({royaltyStartMonth:e.target.value})}>
          <option value="">— Select month —</option>
          {allMonths.reverse().map(m=><option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div><label>HEAD OF CENTRE</label>
        <select value={form.headOfCentre||''} onChange={e=>u({headOfCentre:e.target.value||null})}>
          <option value="">— None —</option>
          {allFranchiseeUsers.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div><label>STATUS</label>
        <div style={{ display:'flex', gap:8 }}>
          {['potential','signed','active','closed'].map(s=>(
            <button key={s} type="button" onClick={()=>u({status:s})} className="btn btn-sm" style={{ background:form.status===s?'var(--navy)':'#fff', color:form.status===s?'#fff':'var(--text-secondary)', border:'1px solid var(--border)', textTransform:'capitalize' }}>{s}</button>
          ))}
        </div>
      </div>
      {form.status === 'closed' && (
        <div><label>CLOSED DATE</label><input value={form.closedDate} placeholder="e.g. Dec 2025" onChange={e=>u({closedDate:e.target.value})} /></div>
      )}
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex:1 }} onClick={() => onSave(form)}>Save Changes</button>
      </div>
    </div>
  );
}

// ─── Edit Owners ───────────────────────────────────────────────────────────────
function EditOwners({ franchise: f, users, onSave, onClose }) {
  const [owners, setOwners] = useState((f.owners||[]).map(o=>({...o})));
  const activeOwners = owners.filter(o=>o.isCurrent!==false);
  const totalShare = activeOwners.reduce((s,o)=>s+(parseFloat(o.sharePercent)||0),0);
  function addOwner() { setOwners(o=>[...o,{name:'',email:'',phone:'',sharePercent:'',effectiveFrom:'',isCurrent:true}]); }
  function removeOwner(i) { setOwners(o=>o.filter((_,j)=>j!==i)); }
  function update(i,k,v) { setOwners(o=>o.map((x,j)=>j===i?{...x,[k]:v}:x)); }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ padding:'10px 14px', background:Math.abs(totalShare-100)<0.01?'var(--green-bg)':'var(--amber-bg)', borderRadius:'var(--r-md)', fontSize:12, fontWeight:500, color:Math.abs(totalShare-100)<0.01?'var(--green)':'var(--amber)' }}>
        Active owners total: {totalShare.toFixed(1)}% {Math.abs(totalShare-100)<0.01?'✓':'— must equal 100%'}
      </div>
      {owners.map((o,i) => (
        <div key={i} style={{ border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:14, background:o.isCurrent===false?'#f9fafb':'#fff' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--navy)' }}>{o.name||`Owner ${i+1}`}</span>
            <div style={{ display:'flex', gap:6 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
                <input type="checkbox" checked={o.isCurrent!==false} onChange={e=>update(i,'isCurrent',e.target.checked)} />
                Current
              </label>
              <button onClick={()=>removeOwner(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:14 }}>✕</button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <div><label>NAME</label><input value={o.name} onChange={e=>update(i,'name',e.target.value)} /></div>
            <div><label>EMAIL</label><input value={o.email} onChange={e=>update(i,'email',e.target.value)} /></div>
            <div><label>PHONE</label><input value={o.phone} onChange={e=>update(i,'phone',e.target.value)} /></div>
            <div><label>SHARE %</label><input type="number" value={o.sharePercent} onChange={e=>update(i,'sharePercent',e.target.value)} /></div>
            <div><label>EFFECTIVE FROM</label><input value={o.effectiveFrom||''} placeholder="e.g. Sep 2025" onChange={e=>update(i,'effectiveFrom',e.target.value)} /></div>
            <div><label>EFFECTIVE TO</label><input value={o.effectiveTo||''} placeholder="e.g. Dec 2025 (if left)" onChange={e=>update(i,'effectiveTo',e.target.value)} /></div>
          </div>
        </div>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={addOwner}>+ Add Owner</button>
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex:1 }} onClick={() => onSave({ owners })}>Save Owners</button>
      </div>
    </div>
  );
}

// ─── Edit Investors ────────────────────────────────────────────────────────────
function EditInvestors({ franchise: f, users, onSave, onClose }) {
  const [investors, setInvestors] = useState((f.investors||[]).map(o=>({...o})));
  function addInvestor() { setInvestors(i=>[...i,{name:'',email:'',phone:'',sharePercent:'',type:'individual',isCurrent:true}]); }
  function removeInvestor(i) { setInvestors(v=>v.filter((_,j)=>j!==i)); }
  function update(i,k,v) { setInvestors(v=>v.map((x,j)=>j===i?{...x,[k]:v}:x)); }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      {investors.map((inv,i) => (
        <div key={i} style={{ border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:14, background:inv.isCurrent===false?'#f9fafb':'#fff' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
            <span style={{ fontSize:12, fontWeight:600, color:'var(--navy)' }}>{inv.name||`Investor ${i+1}`}</span>
            <div style={{ display:'flex', gap:6 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
                <input type="checkbox" checked={inv.isCurrent!==false} onChange={e=>update(i,'isCurrent',e.target.checked)} />
                Current
              </label>
              <button onClick={()=>removeInvestor(i)} style={{ background:'none', border:'none', color:'var(--red)', cursor:'pointer', fontSize:14 }}>✕</button>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            <div><label>NAME</label><input value={inv.name} onChange={e=>update(i,'name',e.target.value)} /></div>
            <div><label>EMAIL</label><input value={inv.email} onChange={e=>update(i,'email',e.target.value)} /></div>
            <div><label>TYPE</label>
              <select value={inv.type||'individual'} onChange={e=>update(i,'type',e.target.value)}>
                <option value="individual">Individual</option>
                <option value="company">Company</option>
              </select>
            </div>
            <div><label>SHARE %</label><input type="number" value={inv.sharePercent} onChange={e=>update(i,'sharePercent',e.target.value)} /></div>
            {inv.type==='company' && <div style={{ gridColumn:'1/-1' }}><label>COMPANY NAME</label><input value={inv.companyName||''} onChange={e=>update(i,'companyName',e.target.value)} /></div>}
            <div><label>EFFECTIVE FROM</label><input value={inv.effectiveFrom||''} placeholder="e.g. Sep 2025" onChange={e=>update(i,'effectiveFrom',e.target.value)} /></div>
          </div>
        </div>
      ))}
      <button className="btn btn-secondary btn-sm" onClick={addInvestor}>+ Add Investor</button>
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex:1 }} onClick={() => onSave({ investors })}>Save Investors</button>
      </div>
    </div>
  );
}

// ─── Add Financials ────────────────────────────────────────────────────────────
function AddFinancials({ franchise, onSave, onClose }) {
  const [form, setForm] = useState({ month:'', income:'', rent:'', salaries:'', royalty:'', otherExpenses:'' });
  const u = e => setForm(p=>({...p,...e}));
  const net = (parseFloat(form.income)||0)-((parseFloat(form.rent)||0)+(parseFloat(form.salaries)||0)+(parseFloat(form.royalty)||0)+(parseFloat(form.otherExpenses)||0));
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
      <div><label>MONTH</label><input value={form.month} placeholder="e.g. May 2026" onChange={e=>u({month:e.target.value})} /></div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        <div><label>INCOME (£)</label><input type="number" value={form.income} onChange={e=>u({income:e.target.value})} /></div>
        <div><label>RENT (£)</label><input type="number" value={form.rent} onChange={e=>u({rent:e.target.value})} /></div>
        <div><label>SALARIES (£)</label><input type="number" value={form.salaries} onChange={e=>u({salaries:e.target.value})} /></div>
        <div><label>ROYALTY (£)</label><input type="number" value={form.royalty} onChange={e=>u({royalty:e.target.value})} /></div>
        <div><label>OTHER EXPENSES (£)</label><input type="number" value={form.otherExpenses} onChange={e=>u({otherExpenses:e.target.value})} /></div>
      </div>
      <div style={{ padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:13 }}>
        Net Profit: <strong style={{ color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</strong>
      </div>
      <div style={{ display:'flex', gap:8, marginTop:8 }}>
        <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" style={{ flex:1 }} onClick={() => onSave(form)} disabled={!form.month}>Save</button>
      </div>
    </div>
  );
}

// ─── Manage Salaries ───────────────────────────────────────────────────────────
function ManageSalaries({ franchise, owners, salaries, users, onSave, onClose }) {
  const [newRows, setNewRows] = useState(owners.map(o => ({ userId:'', amount:'', effectiveFrom:'' })));
  const [expandedHistory, setExpandedHistory] = useState({});

  function getOwnerUserId(owner) {
    return users.find(u => (u.email||'').toLowerCase()===(owner.email||'').toLowerCase())?.id || null;
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
      <p style={{ fontSize:13, color:'var(--text-secondary)' }}>Salaries are deducted from net profit before the share % split is applied.</p>
      {owners.map((o,i) => {
        const uid = getOwnerUserId(o);
        const ownerSals = salaries.filter(s => s.userId === uid).sort((a,b) => parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom));
        const currentSal = ownerSals[0];
        const histKey = o.email;
        return (
          <div key={i} style={{ border:'1px solid var(--border)', borderRadius:'var(--r-md)', padding:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--navy)', marginBottom:4 }}>{o.name}</div>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:12 }}>
              Current salary: <strong style={{ color:currentSal?'var(--green)':'var(--text-tertiary)' }}>{currentSal?`${fmt(currentSal.amount)}/mo from ${currentSal.effectiveFrom}`:'None set'}</strong>
            </div>
            {uid && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, alignItems:'end' }}>
                <div><label>AMOUNT (£/month)</label><input type="number" value={newRows[i]?.amount||''} onChange={e=>setNewRows(r=>r.map((x,j)=>j===i?{...x,amount:e.target.value}:x))} placeholder="e.g. 1500" /></div>
                <div><label>EFFECTIVE FROM</label>
                  <select value={newRows[i]?.effectiveFrom||''} onChange={e=>setNewRows(r=>r.map((x,j)=>j===i?{...x,effectiveFrom:e.target.value}:x))}>
                    <option value="">Select month...</option>
                    {(() => {
                      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                      const opts = [];
                      const now = new Date();
                      for (let delta = -12; delta <= 3; delta++) {
                        const d = new Date(now.getFullYear(), now.getMonth() + delta, 1);
                        const label = months[d.getMonth()] + ' ' + d.getFullYear();
                        opts.push(<option key={label} value={label}>{label}</option>);
                      }
                      return opts;
                    })()}
                  </select>
                </div>
                <button className="btn btn-sm btn-primary" onClick={async () => {
                  const row = newRows[i];
                  if (!row.amount || !row.effectiveFrom) { alert('Enter amount and month'); return; }
                  await api.addSalary(franchise.id, { userId:uid, amount:parseFloat(row.amount), effectiveFrom:row.effectiveFrom });
                  await api.logActivity('Updated salary', `${o.name} — ${fmt(row.amount)}/mo from ${row.effectiveFrom}`);
                  setNewRows(r=>r.map((x,j)=>j===i?{...x,amount:'',effectiveFrom:''}:x));
                  onSave({});
                }} style={{ marginBottom:0 }}>Save</button>
              </div>
            )}
            {!uid && <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>No user account linked to this email</div>}
            {ownerSals.length > 1 && (
              <div style={{ marginTop:10 }}>
                <button onClick={()=>setExpandedHistory(h=>({...h,[histKey]:!h[histKey]}))} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'var(--text-tertiary)', display:'flex', alignItems:'center', gap:4 }}>
                  SALARY HISTORY {expandedHistory[histKey]?'▲':'▼'}
                </button>
                {expandedHistory[histKey] && (
                  <div style={{ marginTop:6 }}>
                    {ownerSals.map((s,si) => (
                      <div key={si} style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-tertiary)', padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                        <span>{s.effectiveFrom}</span>
                        <span style={{ color:'var(--green)', fontWeight:600 }}>{fmt(s.amount)}/mo</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ display:'flex', gap:8 }}>
        <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ─── Bank Details Panel ────────────────────────────────────────────────────────
function BankDetailsPanel({ franchise, onClose, onReload, showToast }) {
  const bd = franchise.bankDetails || null;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ accountName:bd?.accountName||'', sortCode:bd?.sortCode||'', accountNumber:bd?.accountNumber||'' });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  async function save() {
    if (!form.accountName||!form.sortCode||!form.accountNumber) { showToast('All fields required'); return; }
    await api.saveBankDetails(franchise.id, form);
    await api.logActivity('Updated bank details', franchise.name);
    showToast('Bank details saved'); setEditing(false); onReload();
  }

  async function loadHistory() {
    if (!showHistory) {
      const h = await api.getBankHistory(franchise.id).catch(()=>[]);
      setHistory(h||[]);
    }
    setShowHistory(h=>!h);
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
      {!editing && <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button className="btn btn-sm btn-secondary" onClick={() => { setEditing(true); setForm({accountName:bd?.accountName||'',sortCode:bd?.sortCode||'',accountNumber:bd?.accountNumber||''}); }}>
          ✎ {bd?'Edit':'Add Bank Details'}
        </button>
      </div>}
      {!editing && (bd ? (
        <div className="card" style={{ padding:16, borderLeft:'3px solid var(--blue)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
            <div><label>ACCOUNT NAME</label><div style={{ fontSize:14, fontWeight:600 }}>{bd.accountName}</div></div>
            <div><label>SORT CODE</label><div style={{ fontSize:14, fontWeight:600, color:'var(--blue)', letterSpacing:'0.1em' }}>{bd.sortCode}</div></div>
            <div><label>ACCOUNT NUMBER</label><div style={{ fontSize:14, fontWeight:600, color:'var(--blue)', letterSpacing:'0.1em' }}>{bd.accountNumber}</div></div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign:'center', padding:32, border:'1px dashed var(--border)', borderRadius:'var(--r-lg)', color:'var(--text-tertiary)', fontSize:13 }}>No bank details added yet</div>
      ))}
      {editing && (
        <div className="card" style={{ padding:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            <div><label>ACCOUNT NAME *</label><input value={form.accountName} onChange={e=>setForm(f=>({...f,accountName:e.target.value}))} placeholder="e.g. Fine Daycare Ltd" /></div>
            <div><label>SORT CODE *</label><input value={form.sortCode} onChange={e=>setForm(f=>({...f,sortCode:e.target.value}))} placeholder="e.g. 20-45-67" maxLength={8} /></div>
            <div><label>ACCOUNT NUMBER *</label><input value={form.accountNumber} onChange={e=>setForm(f=>({...f,accountNumber:e.target.value}))} placeholder="e.g. 12345678" maxLength={8} /></div>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setEditing(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
          </div>
        </div>
      )}
      <div>
        <button onClick={loadHistory} style={{ background:'none', border:'none', cursor:'pointer', fontSize:11, color:'var(--text-tertiary)', display:'flex', alignItems:'center', gap:4 }}>
          CHANGE HISTORY {showHistory?'▲':'▼'}
        </button>
        {showHistory && (
          <div className="card" style={{ marginTop:8, overflow:'hidden' }}>
            {history.length === 0 ? <div style={{ padding:'14px 16px', fontSize:12, color:'var(--text-tertiary)' }}>No history yet</div> :
              history.map((h,i) => (
                <div key={h.id} style={{ display:'flex', justifyContent:'space-between', padding:'10px 16px', borderBottom:i<history.length-1?'1px solid var(--border)':undefined, fontSize:12 }}>
                  <div>
                    <span style={{ fontWeight:600 }}>{h.accountName}</span>
                    <span style={{ color:'var(--blue)', marginLeft:12 }}>{h.sortCode}</span>
                    <span style={{ color:'var(--blue)', marginLeft:8 }}>{h.accountNumber}</span>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ color:'var(--text-tertiary)', fontSize:11 }}>{h.createdAt?.slice(0,10)||''}</div>
                    {h.updatedBy && <div style={{ color:'var(--text-tertiary)', fontSize:10 }}>{h.updatedBy}</div>}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── New Branch Panel ──────────────────────────────────────────────────────────
function NewBranchPanel({ users, onSave, onClose }) {
  const [form, setForm] = useState({ name:'', location:'', phone:'', email:'', status:'potential', royaltyStartMonth:'' });
  const [owners, setOwners] = useState([{ name:'', email:'', phone:'', sharePercent:'' }]);
  const u = e => setForm(p=>({...p,...e}));
  return (
    <SlidePanel open={true} onClose={onClose} title="New Branch">
      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        <div><label>COMPANY NAME *</label><input value={form.name} onChange={e=>u({name:e.target.value})} placeholder="e.g. Go Childcare Nowhere" /></div>
        <div><label>LOCATION</label><input value={form.location} onChange={e=>u({location:e.target.value})} placeholder="e.g. Nowhere Branch" /></div>
        <div><label>STATUS</label>
          <select value={form.status} onChange={e=>u({status:e.target.value})}>
            {['potential','signed','active'].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <div><label>ROYALTY START MONTH</label><select value={form.royaltyStartMonth} onChange={e=>setForm(f=>({...f,royaltyStartMonth:e.target.value}))}><option value="">— Select month —</option>{(()=>{const MTHS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];const now=new Date();const opts=[];const fd=new Date(now.getFullYear(),now.getMonth()+6);for(let y=2024;y<=fd.getFullYear();y++){const endM=y===fd.getFullYear()?fd.getMonth():11;for(let m=0;m<=endM;m++)opts.push(MTHS[m]+" "+y);}return opts.reverse().map(m=><option key={m} value={m}>{m}</option>);})()}</select></div>
          <div><label>PHONE</label><input value={form.phone} onChange={e=>u({phone:e.target.value})} /></div>
          <div><label>EMAIL</label><input value={form.email} onChange={e=>u({email:e.target.value})} /></div>
        </div>
        <div style={{ borderTop:'1px solid var(--border)', paddingTop:12 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--navy)', marginBottom:10 }}>Owners</div>
          {owners.map((o,i) => (
            <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, marginBottom:8 }}>
              <input placeholder="Name" value={o.name} onChange={e=>{const a=[...owners];a[i]={...a[i],name:e.target.value};setOwners(a);}} />
              <input placeholder="Email" value={o.email} onChange={e=>{const a=[...owners];a[i]={...a[i],email:e.target.value};setOwners(a);}} />
              <input placeholder="%" type="number" value={o.sharePercent} onChange={e=>{const a=[...owners];a[i]={...a[i],sharePercent:e.target.value};setOwners(a);}} style={{ width:60 }} />
            </div>
          ))}
          <button className="btn btn-sm btn-secondary" onClick={()=>setOwners(o=>[...o,{name:'',email:'',phone:'',sharePercent:''}])}>+ Add Owner</button>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <button className="btn btn-secondary" style={{ flex:1 }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex:1 }} onClick={() => onSave({...form, owners})} disabled={!form.name}>Create Branch</button>
        </div>
      </div>
    </SlidePanel>
  );
}

// ─── Declaration Detail View (used in BranchDetail modal) ─────────────────────
function DeclDetailView({ d, fmt }) {
  const hasAmend = d.amendedIncome !== null && d.amendedIncome !== undefined;
  const hasEdit = d.editRequestApproved && !d.editRequested;

  const origIncome = parseFloat(d.income)||0;
  const origRoyalty = parseFloat(d.royalty)||0;
  const origRent = parseFloat(d.rent)||0;
  const origSalaries = parseFloat(d.salaries)||0;
  const origOther = parseFloat(d.otherExpenses)||0;
  const origExp = origRent+origSalaries+origRoyalty+origOther;
  const origNet = origIncome-origExp;
  const origMargin = origIncome > 0 ? (origNet/origIncome*100).toFixed(1) : 0;

  const newIncome = hasAmend && d.amendedIncome!=null ? parseFloat(d.amendedIncome) : origIncome;
  const newRoyalty = hasAmend && d.amendedRoyalty!=null ? parseFloat(d.amendedRoyalty) : origRoyalty;
  const newRent = hasAmend && d.amendedRent!=null ? parseFloat(d.amendedRent) : origRent;
  const newSalaries = hasAmend && d.amendedSalaries!=null ? parseFloat(d.amendedSalaries) : origSalaries;
  const newOther = hasAmend && d.amendedOtherExpenses!=null ? parseFloat(d.amendedOtherExpenses) : origOther;
  const newExp = newRent+newSalaries+newRoyalty+newOther;
  const newNet = newIncome-newExp;
  const newMargin = newIncome > 0 ? (newNet/newIncome*100).toFixed(1) : 0;

  const royaltyDiff = newRoyalty - origRoyalty;

  const rows = [
    ['Income', origIncome, newIncome],
    ['Rent', origRent, newRent],
    ['Salaries', origSalaries, newSalaries],
    ['Royalty', origRoyalty, newRoyalty],
    ['Other', origOther, newOther],
    ['Net Profit', origNet, newNet],
    ['Margin', `${origMargin}%`, `${newMargin}%`],
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {/* Status badges */}
      <div style={{ display:'flex', gap:6 }}>
        {hasAmend && !d.amendmentSettled && <Badge type="amber">⚠ Amendment Pending</Badge>}
        {hasAmend && d.amendmentSettled && <Badge type="green">✓ Amendment Settled</Badge>}
        {hasEdit && <Badge type="blue">✎ Edit Approved</Badge>}
        <StatusBadge status={d.status} />
      </div>

      {hasAmend && (
        <>
          <table className="table" style={{ fontSize:12 }}>
            <thead><tr>
              <th>FIELD</th>
              <th style={{ textAlign:'right', color:'var(--red)' }}>ORIGINAL</th>
              <th style={{ textAlign:'right', color:'var(--blue)' }}>CORRECTED</th>
              <th style={{ textAlign:'right' }}>DIFFERENCE</th>
            </tr></thead>
            <tbody>
              {rows.map(([label, orig, corr]) => {
                const isStr = typeof orig === 'string';
                const diff = isStr ? null : corr - orig;
                const changed = isStr ? orig !== corr : Math.abs(diff) > 0.01;
                return (
                  <tr key={label} style={{ background: changed ? 'rgba(37,99,235,0.03)' : undefined }}>
                    <td style={{ fontWeight: changed ? 600 : 400 }}>{label}</td>
                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', color: changed ? 'var(--text-tertiary)' : undefined, textDecoration: changed ? 'line-through' : undefined }}>
                      {isStr ? orig : fmt(orig)}
                    </td>
                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontWeight: changed ? 700 : 400, color: changed ? 'var(--blue)' : undefined }}>
                      {isStr ? corr : fmt(corr)}
                    </td>
                    <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', color: diff > 0 ? 'var(--red)' : diff < 0 ? 'var(--green)' : 'var(--text-tertiary)' }}>
                      {diff === null ? '—' : diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${fmt(diff)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Royalty difference callout */}
          {Math.abs(royaltyDiff) > 0.01 && (
            <div style={{ padding:'12px 14px', borderRadius:'var(--r-md)', background: royaltyDiff > 0 ? 'var(--amber-bg)' : 'var(--green-bg)', border: `1px solid ${royaltyDiff > 0 ? 'rgba(217,119,6,0.3)' : 'rgba(15,110,86,0.2)'}`, fontSize:13, fontWeight:600 }}>
              {royaltyDiff > 0
                ? `⚠ Additional royalty to collect: ${fmt(royaltyDiff)}`
                : `✓ Refund owed to franchisee: ${fmt(Math.abs(royaltyDiff))}`
              }
            </div>
          )}

          {d.amendmentSettled && (
            <div style={{ padding:'10px 14px', borderRadius:'var(--r-md)', background:'var(--green-bg)', border:'1px solid rgba(15,110,86,0.2)', fontSize:12, color:'var(--green)', fontWeight:600 }}>
              ✓ This amendment has been settled
            </div>
          )}
        </>
      )}

      {hasEdit && !hasAmend && (
        <div style={{ padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:12 }}>
          This declaration was edited after submission. The figures shown are the approved corrected version.
        </div>
      )}
    </div>
  );
}

// ─── Investor P&L Table ────────────────────────────────────────────────────────
function InvestorPLTable({ franchise, investors, owners, plFinancials, plYear, setPlYear, getSalary, getOwnerUserId, isAdmin, showToast, onReload }) {
  const [payments, setPayments] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    api.getInvestorPayments(franchise.id).then(data => {
      if (data?.payments) {
        const map = {};
        data.payments.forEach(p => { map[p.month] = { paid: p.paid, datePaid: p.date_paid ? p.date_paid.split('T')[0] : '', method: p.method||'' }; });
        setPayments(map);
      }
    }).catch(() => {});
  }, [franchise.id]);

  async function updatePayment(month, field, value) {
    const current = payments[month] || { paid: false, datePaid: '', method: '' };
    const updated = { ...current, [field]: value };
    setPayments(p => ({ ...p, [month]: updated }));
    setSaving(s => ({ ...s, [month]: true }));
    await api.saveInvestorPayment(franchise.id, { month, paid: updated.paid, datePaid: updated.datePaid, method: updated.method }).catch(() => {});
    setSaving(s => ({ ...s, [month]: false }));
  }

  const paidCount = plFinancials.filter(m => payments[m.month]?.paid).length;

  return (
    <div className="card" style={{ marginTop:16 }}>
      <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
        <span style={{ fontSize:13, fontWeight:600, color:'var(--blue)' }}>Investor Profit & Loss</span>
        <div style={{ display:'flex', gap:4, alignItems:'center' }}>
          <button onClick={()=>setPlYear(y=>y-1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>‹</button>
          <span style={{ fontSize:12, fontWeight:600, minWidth:36, textAlign:'center' }}>{plYear}</span>
          <button onClick={()=>setPlYear(y=>y+1)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-tertiary)', fontSize:18 }}>›</button>
        </div>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>MONTH</th>
              <th style={{ textAlign:'right' }}>NET PROFIT</th>
              <th style={{ textAlign:'right' }}>AFTER SALARIES</th>
              {investors.map((inv,i) => (
                <th key={i} style={{ textAlign:'right', color:'var(--blue)', minWidth:110 }}>
                  {(inv.companyName||inv.name||'Investor').split(' ').slice(0,2).join(' ')}
                  <div style={{ fontSize:9, color:'var(--text-tertiary)', fontWeight:400 }}>{inv.sharePercent}%</div>
                </th>
              ))}
              <th style={{ textAlign:'center' }}>PAID</th>
              <th>DATE PAID</th>
              <th>METHOD</th>
            </tr>
          </thead>
          <tbody>
            {plFinancials.map(m => {
              const net = (parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
              const allOwnerSals = owners.reduce((s,o) => s + getSalary(getOwnerUserId(o), m.month), 0);
              const afterSals = net - allOwnerSals;
              const pmt = payments[m.month] || { paid:false, datePaid:'', method:'' };
              return (
                <tr key={m.month} style={{ background: pmt.paid ? 'rgba(15,110,86,0.03)' : undefined }}>
                  <td style={{ fontSize:12 }}>{m.month}</td>
                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, color:'var(--text-secondary)' }}>{fmt(afterSals)}</td>
                  {investors.map((inv,ii) => {
                    const invShare = afterSals * parseFloat(inv.sharePercent||0)/100;
                    return (
                      <td key={ii} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:invShare>=0?'var(--blue)':'var(--red)' }}>
                        {fmt(invShare)}
                      </td>
                    );
                  })}
                  <td style={{ textAlign:'center' }}>
                    {isAdmin ? (
                      <button onClick={() => updatePayment(m.month, 'paid', !pmt.paid)}
                        style={{ padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:600, cursor:'pointer', border:'none',
                          background: pmt.paid ? 'var(--green-bg)' : 'rgba(163,45,45,0.08)',
                          color: pmt.paid ? 'var(--green)' : 'var(--red)',
                          border: `1px solid ${pmt.paid ? 'rgba(15,110,86,0.2)' : 'rgba(163,45,45,0.2)'}` }}>
                        {pmt.paid ? '✓ Paid' : 'No'}
                      </button>
                    ) : (
                      <span style={{ padding:'3px 12px', borderRadius:20, fontSize:11, fontWeight:600,
                        background: pmt.paid ? 'var(--green-bg)' : 'rgba(163,45,45,0.08)',
                        color: pmt.paid ? 'var(--green)' : 'var(--red)' }}>
                        {pmt.paid ? '✓ Paid' : 'No'}
                      </span>
                    )}
                  </td>
                  <td>
                    {isAdmin ? (
                      <input type="date" value={pmt.datePaid||''} onChange={e => updatePayment(m.month, 'datePaid', e.target.value)}
                        style={{ width:130, padding:'4px 6px', fontSize:11, marginBottom:0 }} />
                    ) : (
                      <span style={{ fontSize:12, color:'var(--text-secondary)' }}>
                        {pmt.datePaid ? new Date(pmt.datePaid).toLocaleDateString('en-GB') : '—'}
                      </span>
                    )}
                  </td>
                  <td>
                    {isAdmin ? (
                      <select value={pmt.method||''} onChange={e => updatePayment(m.month, 'method', e.target.value)}
                        style={{ width:130, padding:'4px 6px', fontSize:11, marginBottom:0 }}>
                        <option value="">Select...</option>
                        {['Bank Transfer','Card','Cash','Cheque'].map(m2=><option key={m2} value={m2}>{m2}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize:12, color:'var(--text-secondary)' }}>{pmt.method||'—'}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="total-row" style={{ background:'var(--blue-bg)', fontWeight:700 }}>
              <td style={{ fontSize:12 }}>YEARLY TOTAL {plYear}</td>
              <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                {fmt(plFinancials.reduce((s,m)=>s+(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0)),0))}
              </td>
              <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                {fmt(plFinancials.reduce((s,m)=>{
                  const net=(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                  return s+net-owners.reduce((os,o)=>os+getSalary(getOwnerUserId(o),m.month),0);
                },0))}
              </td>
              {investors.map((inv,ii) => {
                const total = plFinancials.reduce((s,m)=>{
                  const net=(parseFloat(m.income)||0)-((parseFloat(m.rent)||0)+(parseFloat(m.salaries)||0)+(parseFloat(m.royalty)||0)+(parseFloat(m.otherExpenses)||0));
                  const allSals=owners.reduce((os,o)=>os+getSalary(getOwnerUserId(o),m.month),0);
                  return s+(net-allSals)*parseFloat(inv.sharePercent||0)/100;
                },0);
                return <td key={ii} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:13, fontWeight:700, color:total>=0?'var(--blue)':'var(--red)' }}>{fmt(total)}</td>;
              })}
              <td colSpan={3} style={{ fontSize:11, color:'var(--text-tertiary)' }}>{paidCount} / {plFinancials.length} paid</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
